<!--
  Override of `@apexl/icarus-core`'s generic `ContentTitle.vue`.

  Adds the status-icon-in-a-circle treatment the approved auth-screens
  mockup (`design/surfaces/auth-screens.html`, states 5/7/8) shows above
  the check-email / expired-link / reset-success cards. The three routes
  that render those cards are distinguishable purely by URL, which is the
  only reliable signal available here — `ContentTitle` is a generic,
  everywhere-used component with no per-usage "kind" prop, and the real
  `ForgotPasswordCheckEmailDisplayController` / `ResetPasswordDisplayController`
  (expired branch) / `ResetPasswordSuccessDisplayController` all render
  plain text with no icon marker of their own (see the mockup's "Design
  decisions worth flagging" note — this icon treatment is net-new chrome,
  not sourced from io-user). Every other `ContentTitle` usage in the app
  falls through `statusIcon === null` and renders exactly as core does.
-->
<template>
    <div :class="classes" class="contentTile">
        <div v-if="statusIcon" class="status-icon" :class="statusIcon">
          <svg v-if="statusIcon === 'mail'" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2.5" y="4" width="13" height="10" rx="1.5"/><path d="M2.5 5l6.5 5 6.5-5"/></svg>
          <svg v-else-if="statusIcon === 'warning'" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="9" cy="9" r="7"/><path d="M9 5.5v4M9 12.2v.1"/></svg>
          <svg v-else viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M4 9.5l3 3 7-7"/></svg>
        </div>
        <div v-if="syntheticTitle" class="title" v-html="wrappedTitle"/>
        <div v-if="parsedContent" class="content">
            <template v-if="typeof parsedContent === 'object' && parsedContent !== null">
                <component :is="parsedContent.component.name"
                           v-if="parsedContent.component"
                           :class="parsedContent.component.classes"
                           :components="parsedContent.component.components"
                           v-bind="parsedContent.component.props"/>
            </template>
            <p v-else v-html="parsedContent"/>
        </div>
    </div>
</template>
<script setup>
import {computed, defineProps} from 'vue';
import {useRoute} from 'vue-router';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';

const props = defineProps({
  ...commonProps,
  header: {
    type: Number,
    default: 3
  },
  title: {
    type: String,
    default: '',
  },
  content: {
    type: [String, Object],
    default: '',
  }
})
const route = useRoute();

// `ForgotPasswordCheckEmailDisplayController` sends no `title` prop at all
// (only `content`) — the mockup's "Check your email" heading is aspirational
// chrome, same category as the wordmark/card, not sourced from io-user.
// Every other real `title` (e.g. "Your reset link has expired") passes
// through unchanged.
const syntheticTitle = computed(() => {
  if (props.title) {
    return props.title;
  }
  if (route.path === '/forgot-password/check-email') {
    return 'Check your email';
  }
  return '';
});
const wrappedTitle = computed(() => `<h${props.header}>${syntheticTitle.value}</h${props.header}>`);
const parsedContent = computed(() => {
  if (!props.content) {
    return '';
  }
  try {
    return {
      component: JSON.parse(props.content)
    };
  } catch (e) {
    return props.content;
  }
})

const statusIcon = computed(() => {
  if (route.path === '/forgot-password/check-email') return 'mail';
  if (route.path === '/reset-password-success') return 'success';
  if (route.path.startsWith('/reset-password/')) return 'warning';
  return null;
});
</script>

<style lang="scss">
.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;

  svg {
    width: 22px;
    height: 22px;
  }

  &.mail,
  &.success {
    background: rgba(47, 163, 107, .12);
    color: var(--theme-status-success);
  }

  &.warning {
    background: rgba(224, 160, 48, .14);
    color: var(--theme-status-warning);
  }
}
</style>
